import {useQuery} from "@apollo/client";
import React, {useEffect} from "react";
import {FieldErrors, FormProvider, useForm} from "react-hook-form";

import {InputField} from "@/components/form/fields/InputField/InputField.tsx";
import FormErrorAlerts from "@/components/form/FormErrorAlerts.tsx";
import {ServiceField} from "@/lib/constants/service.ts";
import {GQL_GET_SERVICE_DEFINITIONS} from "@/lib/queries/checks.ts";
import {ServiceDefinition, ServiceFieldDefinition} from "@/lib/types/checks.ts";
import {ServerErrors} from "@/lib/types/common.ts";
import {PercentageField} from "@/pages/checks/components/form/fields/PercentageField.tsx";
import {EditCheckSlaFormValues} from "@/pages/checks/list/actions/bulkSetSLA/BulkSetSlaModal.tsx";
import {setServerErrors} from "@/pages/vault/vaultForm/CredentialFormUtils.ts";

const DEFAULT_SLA_VALUE = "0.9900";

export interface BulkSetSlaFormProps {
  formId: string;
  onSubmit: (data: EditCheckSlaFormValues) => void;
  onError?: (errors: FieldErrors<EditCheckSlaFormValues>, event?: React.BaseSyntheticEvent) => void;
  serverErrors?: ServerErrors;
}

export const BulkSetSlaForm = ({formId, onSubmit, onError, serverErrors}: BulkSetSlaFormProps) => {
  const {data} = useQuery<{serviceDefinitions: ServiceDefinition[]}>(GQL_GET_SERVICE_DEFINITIONS);
  const [nonFieldErrors, setNonFieldErrors] = React.useState<string[]>([]);

  let mspUptimeSlaField: ServiceFieldDefinition | undefined;
  let mspResponseTimeSlaField: ServiceFieldDefinition | undefined;

  data?.serviceDefinitions.forEach((serviceDefinition) => {
    serviceDefinition.fields.forEach((field) => {
      if (field.field === "msp_uptime_sla" && !mspUptimeSlaField) {
        mspUptimeSlaField = field;
      }
      if (field.field === "msp_response_time_sla" && !mspResponseTimeSlaField) {
        mspResponseTimeSlaField = field;
      }
    });
  });

  const methods = useForm<EditCheckSlaFormValues>({
    defaultValues: {
      msp_uptime_sla: DEFAULT_SLA_VALUE,
      msp_response_time_sla: undefined,
    },
  });

  useEffect(() => {
    if (serverErrors) {
      const errorsNotInForm = setServerErrors(serverErrors, methods.setError, "", [
        ServiceField.UPTIME_SLA,
        ServiceField.RESPONSE_TIME_SLA,
      ]);
      setNonFieldErrors(errorsNotInForm);
    }
  }, [serverErrors, methods.setError]);

  const errors = serverErrors ? nonFieldErrors : [];

  return (
    <FormProvider {...methods}>
      <form
        id={formId}
        onSubmit={(e) => {
          methods.handleSubmit(onSubmit, onError)(e);
        }}
      >
        {errors.length > 0 && <FormErrorAlerts errors={errors} />}
        <div className="row mb-4">
          <div className="col fst-italic">
            Note: The current SLA values on selected checks will be replaced with the new settings chosen below.
          </div>
        </div>

        <div className="row">
          <div className="my-1 col-12">
            <PercentageField
              name={ServiceField.UPTIME_SLA}
              label={"Target SLA %"}
              tooltip={mspUptimeSlaField?.title || ""}
              required={false}
            />
          </div>
          <div className="my-1 col-12">
            <InputField
              name={ServiceField.RESPONSE_TIME_SLA}
              label="Target Response Time SLA (s)"
              tooltip={mspResponseTimeSlaField?.title || ""}
              required={false}
              placeholder={"no change"}
            />
          </div>
        </div>
      </form>
    </FormProvider>
  );
};
